import { playerlistAtom, serverMutexAtom } from "@/hooks/playerlist";
import cleanPlayerName from "@shared/cleanPlayerName";
import { useAtomValue } from "jotai";
import { VirtualItem, useVirtualizer } from '@tanstack/react-virtual';
import { memo, useMemo, useRef, useState } from "react";
import { PlayerlistPlayerType } from "@shared/socketioTypes";
import { Input } from "@/components/ui/input";
import { XIcon } from "lucide-react";
import { ScrollArea } from "@/components/ui/scroll-area";
import { cn } from "@/lib/utils";
import { useOpenPlayerModal } from "@/hooks/playerModal";
import { useEventListener } from "usehooks-ts";
import { useCloseAllSheets } from '@/hooks/sheets';
import { usePlayerDossier } from '@/hooks/PlayerDossierProvider';


type PlayerlistFilterProps = {
    filterString: string;
    setFilterString: (s: string) => void;
};
function PlayerlistFilter({ filterString, setFilterString }: PlayerlistFilterProps) {
    const inputRef = useRef<HTMLInputElement>(null);
    useEventListener('message', (e: TxMessageEvent) => {
        if (e.data.type === 'globalHotkey' && e.data.action === 'focusPlayerlistFilter') {
            inputRef.current?.focus();
        }
    });

    return (
        <div className="fc-rail-filter">
            <Input
                ref={inputRef}
                placeholder="Name oder FC-ID filtern"
                value={filterString}
                onChange={(e) => setFilterString(e.target.value)}
                onKeyDown={(e) => {
                    if (e.key === 'Escape') {
                        setFilterString('');
                    }
                }}
            />
            {filterString ? (
                <button
                    type="button"
                    className="fc-rail-filter-clear"
                    aria-label="Filter leeren"
                    onClick={() => setFilterString('')}
                >
                    <XIcon />
                </button>
            ) : (
                <div className="fc-rail-filter-hint">
                    <kbd>ctrl+k</kbd>
                </div>
            )}
        </div>
    );
}
const PlayerlistFilterMemo = memo(PlayerlistFilter);


type PlayerlistPlayerProps = {
    virtualItem: VirtualItem,
    player: PlayerlistPlayerType,
    modalOpener: (netid: number) => void,
}
function PlayerlistPlayer({ virtualItem, player, modalOpener }: PlayerlistPlayerProps) {
    return (
        <div
            className="player"
            role="button"
            tabIndex={0}
            style={{
                height: `${virtualItem.size - 5}px`,
                transform: `translateY(${virtualItem.start}px)`,
            }}
            onClick={() => modalOpener(player.netid)}
            onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    modalOpener(player.netid);
                }
            }}
        >
            <div className="pid-block">
                <span className="pid-badge">{player.permanentId ?? '–'}</span>
            </div>
            <span className="pname">{player.displayName}</span>
        </div>
    )
}


export default function Playerlist() {
    const playerlist = useAtomValue(playerlistAtom);
    const serverMutex = useAtomValue(serverMutexAtom);
    const openPlayerModal = useOpenPlayerModal();
    const closeAllSheets = useCloseAllSheets();
    const { openDossier } = usePlayerDossier();
    const scrollRef = useRef<HTMLDivElement>(null);
    const [filterString, setFilterString] = useState('');

    const filteredPlayerlist = useMemo(() => {
        const pureFilter = cleanPlayerName(filterString).pureName;
        if (pureFilter !== 'emptyname') {
            return playerlist.filter((player) => {
                return player.pureName.includes(pureFilter)
                    || (player.permanentId !== null && player.permanentId.toString().includes(pureFilter));
            })
        } else {
            return playerlist;
        }
    }, [playerlist, filterString]);

    const injectedStyle = useMemo(() => {
        const maxId = Math.max(1, playerlist.reduce((max, player) => Math.max(max, player.permanentId ?? 0), 0));
        const idCharLength = Math.floor(Math.log10(maxId)) + 1;
        return `.tx-playerlist .player .pid-block { min-width: ${idCharLength + 2}ch; }`;
    }, [playerlist]);

    const rowVirtualizer = useVirtualizer({
        isScrollingResetDelay: 0,
        count: filteredPlayerlist.length,
        getScrollElement: () => (scrollRef.current as HTMLDivElement)?.getElementsByTagName('div')[0],
        estimateSize: () => 40,
        overscan: 15,
    });
    const virtualItems = rowVirtualizer.getVirtualItems();

    const modalOpener = (netid: number) => {
        const selected = playerlist.find((player) => player.netid === netid);
        if (selected?.permanentId !== null && selected?.permanentId !== undefined) {
            closeAllSheets();
            openDossier({ publicId: selected.permanentId, name: selected.displayName });
            return;
        }
        if (!serverMutex) return;
        openPlayerModal({ mutex: serverMutex, netid });
    }

    return (
        <>
            <PlayerlistFilterMemo filterString={filterString} setFilterString={setFilterString} />

            <div
                className={cn(
                    'fc-rail-note warn',
                    filteredPlayerlist.length !== playerlist.length && virtualItems.length ? 'block' : 'hidden'
                )}
            >
                {filteredPlayerlist.length} von {playerlist.length} Spielern
            </div>
            <div className={cn('fc-rail-empty', virtualItems.length ? 'hidden' : 'block')}>
                {playerlist.length && filterString ? (
                    <p>
                        Keine passenden Spieler.
                        <span>Filter leeren, um alle Spieler anzuzeigen.</span>
                    </p>
                ) : (
                    <p>
                        Keine Spieler online.
                        <span>Die Liste aktualisiert sich automatisch.</span>
                    </p>
                )}
            </div>

            <style>{injectedStyle}</style>
            <ScrollArea className="h-full select-none" ref={scrollRef}>
                <div
                    className="tx-playerlist"
                    style={{
                        height: `${rowVirtualizer.getTotalSize()}px`,
                        width: '100%',
                        position: 'relative',
                    }}
                >
                    {virtualItems.map((virtualItem) => (
                        <PlayerlistPlayer
                            key={virtualItem.key}
                            virtualItem={virtualItem}
                            player={filteredPlayerlist[virtualItem.index]}
                            modalOpener={modalOpener}
                        />
                    ))}
                </div>
            </ScrollArea>
        </>
    );
}
